import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

export const ArtworkComplianceBlog = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Pharma Artwork Compliance Checklist | TheApostrophe</title>
        <meta name="description" content="Follow this exact checklist to ensure your pharma artwork is regulator-ready and avoid delays." />
      </Helmet>

      <section className="pt-32 pb-16 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 mb-8 transition-colors duration-200 hover:opacity-70" style={{ color: '#708080' }}>
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
          <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white mb-6" style={{ backgroundColor: '#708080' }}>
            Compliance
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
            Pharma Artwork Compliance: The Exact Checklist You Should Follow
          </h1>
          <p className="text-xl" style={{ color: '#708080' }}>
            Jan 2025 · 5 min read
          </p>
        </div>
      </section>

      <section className="pb-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none" style={{ color: '#303040' }}>
              <p className="text-xl leading-relaxed mb-8" style={{ color: '#708080' }}>
                Artwork is one of the most common reasons pharma and nutraceutical dossiers get delayed or rejected. A small mistake — a missing warning, a mismatched claim, or an incorrect translation — can trigger avoidable queries and force reprints, redesigns, or full resubmissions.
              </p>

              <h2 className="text-3xl font-bold mb-6 mt-12" style={{ color: '#708080' }}>
                The Essential Artwork Compliance Checklist
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>1. All Mandatory Information Present</h3>
                    <p>Brand & generic name, composition, pack size & strength, manufacturer details, batch number, dates, storage conditions, route of administration.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>2. Claims Aligned with Dossier</h3>
                    <p>Only include claims you have submitted and received approval for. Statements like "fast acting" or "clinically proven" often require evidence.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>3. Country-Specific Warnings</h3>
                    <p>Include mandatory warnings: keep out of reach of children, do not exceed recommended dose, shake well before use.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>4. Clear and Readable Text</h3>
                    <p>Use readable font sizes and maintain good contrast so all critical information is visible.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>5. Correct Barcodes/QR Codes</h3>
                    <p>Check whether your target country needs a barcode, 2D code, or serialization. Ensure scannability.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>6. Accurate Translations</h3>
                    <p>Labels and inserts must reflect approved wording in the correct local language wherever applicable.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: '#708080' }} />
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>7. Match Artwork with Dossier</h3>
                    <p>Everything printed must match what's in the dossier: composition, storage, strength, manufacturer name, dosage statements.</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Link to="/services">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0" style={{ background: 'linear-gradient(135deg, #60609015 0%, #60609005 100%)' }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#606090' }}>
                  Artwork Services
                </h3>
                <p className="mb-4" style={{ color: '#708080' }}>
                  Get compliant artwork, labels, and translations for your target markets.
                </p>
                <span className="inline-flex items-center font-semibold" style={{ color: '#606090' }}>
                  View Services <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Card>
            </Link>
            <Link to="/contact">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0" style={{ background: 'linear-gradient(135deg, #A060B015 0%, #A060B005 100%)' }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#A060B0' }}>
                  Artwork Review
                </h3>
                <p className="mb-4" style={{ color: '#708080' }}>
                  Get your artwork checked before submission.
                </p>
                <span className="inline-flex items-center font-semibold" style={{ color: '#A060B0' }}>
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};